import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
} from '@mui/material';
import {
  Visibility,
  Delete,
  Download,
  AttachMoney,
  People,
  TrendingUp,
  CalendarToday,
  ArrowUpward,
  ArrowDownward,
  Assessment,
  AccountBalance,
  ShowChart,
  EmojiEvents,
} from '@mui/icons-material';
import { payrollAPI } from '../services/api';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const TabPanel = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box>{children}</Box>}
  </div>
);

const PayrollPage = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [payrolls, setPayrolls] = useState([]);
  const [allPayrolls, setAllPayrolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Dashboard statistics
  const [stats, setStats] = useState({
    totalPayrolls: 0,
    totalStaff: 0,
    totalNetSalary: 0,
    totalBaseSalary: 0,
    totalTax: 0,
    totalAllowances: 0,
    totalDeductions: 0,
    averageSalary: 0,
    recentPayrolls: [],
    // New stats
    monthOverMonth: {
      netSalaryChange: 0,
      netSalaryChangePercent: 0,
      staffCountChange: 0,
      staffCountChangePercent: 0,
    },
    yearToDate: {
      totalPayrolls: 0,
      totalNetSalary: 0,
    },
    topEarners: [],
    departmentBreakdown: [],
    monthlyTrends: [],
    payrollByMonth: [],
  });

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

  useEffect(() => {
    if (tab === 1) {
      fetchPayrolls();
    }
  }, [tab, page, rowsPerPage]);

  useEffect(() => {
    if (tab === 0) {
      fetchDashboardStats();
    }
  }, [tab]);

  const fetchDashboardStats = async () => {
    try {
      setStatsLoading(true);
      const { data } = await payrollAPI.getAll({ limit: 1000 });
      const allPayrollData = data.data || [];
      setAllPayrolls(allPayrollData);

      // Calculate basic statistics
      const totalPayrolls = allPayrollData.length;
      const totalStaff = allPayrollData.reduce((sum, p) => sum + (p.staff_count || 0), 0);
      const totalNetSalary = allPayrollData.reduce((sum, p) => sum + parseFloat(p.total_net_salary || 0), 0);
      const totalBaseSalary = allPayrollData.reduce((sum, p) => sum + parseFloat(p.total_base_salary || 0), 0);
      const totalTax = allPayrollData.reduce((sum, p) => sum + parseFloat(p.total_tax || 0), 0);
      const totalAllowances = allPayrollData.reduce((sum, p) => sum + parseFloat(p.total_allowances || 0), 0);
      const totalDeductions = allPayrollData.reduce((sum, p) => sum + parseFloat(p.total_deductions || 0), 0);
      const averageSalary = totalStaff > 0 ? totalNetSalary / totalStaff : 0;
      const recentPayrolls = allPayrollData.slice(0, 5);

      // Month-over-month comparison
      let monthOverMonth = {
        netSalaryChange: 0,
        netSalaryChangePercent: 0,
        staffCountChange: 0,
        staffCountChangePercent: 0,
      };
      if (allPayrollData.length >= 2) {
        const sortedPayrolls = [...allPayrollData].sort((a, b) => new Date(b.payroll_month) - new Date(a.payroll_month));
        const currentMonth = sortedPayrolls[0];
        const previousMonth = sortedPayrolls[1];
        
        if (currentMonth && previousMonth) {
          const netSalaryChange = parseFloat(currentMonth.total_net_salary || 0) - parseFloat(previousMonth.total_net_salary || 0);
          const netSalaryChangePercent = parseFloat(previousMonth.total_net_salary || 0) > 0 
            ? (netSalaryChange / parseFloat(previousMonth.total_net_salary || 0)) * 100 
            : 0;
          const staffCountChange = (currentMonth.staff_count || 0) - (previousMonth.staff_count || 0);
          const staffCountChangePercent = previousMonth.staff_count > 0 
            ? (staffCountChange / previousMonth.staff_count) * 100 
            : 0;
          
          monthOverMonth = {
            netSalaryChange,
            netSalaryChangePercent,
            staffCountChange,
            staffCountChangePercent,
          };
        }
      }

      // Year-to-date totals
      const currentYear = new Date().getFullYear();
      const yearToDatePayrolls = allPayrollData.filter(p => p.payroll_month && p.payroll_month.startsWith(currentYear.toString()));
      const yearToDateTotal = yearToDatePayrolls.reduce((sum, p) => sum + parseFloat(p.total_net_salary || 0), 0);

      // Department breakdown
      const departmentMap = {};
      allPayrollData.forEach(payroll => {
        if (payroll.items) {
          payroll.items.forEach(item => {
            const dept = item.department || 'Unassigned';
            if (!departmentMap[dept]) {
              departmentMap[dept] = { count: 0, totalSalary: 0, staff: [] };
            }
            departmentMap[dept].count += 1;
            departmentMap[dept].totalSalary += parseFloat(item.net_salary || 0);
            departmentMap[dept].staff.push({
              name: item.staff_name,
              salary: parseFloat(item.net_salary || 0),
            });
          });
        }
      });
      const departmentBreakdown = Object.entries(departmentMap).map(([name, data]) => ({
        name,
        count: data.count,
        totalSalary: data.totalSalary,
        averageSalary: data.count > 0 ? data.totalSalary / data.count : 0,
      })).sort((a, b) => b.totalSalary - a.totalSalary);

      // Top earners across all payrolls
      const allStaff = [];
      allPayrollData.forEach(payroll => {
        if (payroll.items) {
          payroll.items.forEach(item => {
            allStaff.push({
              name: item.staff_name,
              department: item.department,
              salary: parseFloat(item.net_salary || 0),
              month: payroll.payroll_month,
            });
          });
        }
      });
      const topEarners = allStaff
        .sort((a, b) => b.salary - a.salary)
        .slice(0, 10);

      // Monthly trends
      const monthlyTrends = allPayrollData
        .sort((a, b) => new Date(a.payroll_month) - new Date(b.payroll_month))
        .map(p => ({
          month: p.payroll_month,
          netSalary: parseFloat(p.total_net_salary || 0),
          baseSalary: parseFloat(p.total_base_salary || 0),
          staffCount: p.staff_count || 0,
        }));

      // Payroll by month for pie chart
      const payrollByMonth = allPayrollData.map(p => ({
        name: p.payroll_month,
        value: parseFloat(p.total_net_salary || 0),
      }));

      setStats({
        totalPayrolls,
        totalStaff,
        totalNetSalary,
        totalBaseSalary,
        totalTax,
        totalAllowances,
        totalDeductions,
        averageSalary,
        recentPayrolls,
        monthOverMonth,
        yearToDate: {
          totalPayrolls: yearToDatePayrolls.length,
          totalNetSalary: yearToDateTotal,
        },
        topEarners,
        departmentBreakdown,
        monthlyTrends,
        payrollByMonth,
      });
    } catch (err) {
      toast.error('Failed to load dashboard statistics');
      console.error(err);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchPayrolls = async () => {
    try {
      setLoading(true);
      const { data } = await payrollAPI.getAll({ page: page + 1, limit: rowsPerPage });
      setPayrolls(data.data || []);
      setTotal(data.pagination?.total || 0);
    } catch (err) {
      toast.error('Failed to load payrolls');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (id) => {
    try {
      const { data } = await payrollAPI.getById(id);
      setSelectedPayroll(data.data);
      setDetailDialogOpen(true);
    } catch (err) {
      toast.error('Failed to load payroll details');
      console.error(err);
    }
  };

  const handleDeletePayroll = async (id) => {
    if (!window.confirm('Are you sure you want to delete this payroll?')) return;
    
    try {
      await payrollAPI.delete(id);
      toast.success('Payroll deleted successfully');
      fetchPayrolls();
    } catch (err) {
      toast.error('Failed to delete payroll');
      console.error(err);
    }
  };

  const handleExportPayroll = async (payroll) => {
    try {
      // Fetch full payroll details to get items
      const { data } = await payrollAPI.getById(payroll.id);
      const fullPayroll = data.data;
      
      if (!fullPayroll.items || fullPayroll.items.length === 0) {
        toast.error('No payroll items to export');
        return;
      }

      const exportData = fullPayroll.items.map(item => {
        // Parse JSON fields
        const allowances = item.allowances || [];
        const individualDeductions = item.individual_deductions || [];
        const globalDeductions = item.global_deductions || [];
        
        return {
          'Company ID': item.company_id,
          'Name': item.staff_name,
          'Account Number': item.account_number || '',
          'Department': item.department,
          'Position': item.position,
          'Status': item.status,
          'Base Salary': parseFloat(item.base_salary || 0),
          'Tax': parseFloat(item.tax || 0),
          'Commission': parseFloat(item.commission || 0),
          'Total Allowances': parseFloat(item.total_allowances || 0),
          'Allowance Details': allowances.map(a => `${a.name}: $${a.amount}`).join('; ') || 'None',
          'Total Deductions': parseFloat(item.total_deductions || 0),
          'Individual Deduction Details': individualDeductions.map(d => `${d.name}: ${d.percentage}%`).join('; ') || 'None',
          'Global Deduction Details': globalDeductions.map(d => `${d.name}: ${d.percentage}%`).join('; ') || 'None',
          'Net Salary': parseFloat(item.net_salary || 0),
        };
      });

      // Add totals row
      exportData.push({
        'Company ID': '',
        'Name': 'TOTALS',
        'Account Number': '',
        'Department': '',
        'Position': '',
        'Status': '',
        'Base Salary': parseFloat(fullPayroll.total_base_salary || 0),
        'Tax': parseFloat(fullPayroll.total_tax || 0),
        'Commission': parseFloat(fullPayroll.total_commission || 0),
        'Total Allowances': parseFloat(fullPayroll.total_allowances || 0),
        'Allowance Details': '',
        'Total Deductions': parseFloat(fullPayroll.total_deductions || 0),
        'Individual Deduction Details': '',
        'Global Deduction Details': '',
        'Net Salary': parseFloat(fullPayroll.total_net_salary || 0),
      });

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Payroll');
      XLSX.writeFile(workbook, `payroll_${fullPayroll.payroll_month}.xlsx`);
      toast.success('Payroll exported successfully');
    } catch (err) {
      toast.error('Failed to export payroll');
      console.error(err);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h3" fontWeight={700} sx={{ mb: 1, color: 'primary.main' }}>
            Payroll Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Overview and manage payroll records
          </Typography>
        </Box>
        <Button variant="contained" onClick={() => navigate('/staffs')}>
          Create New Payroll
        </Button>
      </Box>

      {/* Tabs */}
      <Tabs value={tab} onChange={(_, newValue) => setTab(newValue)} sx={{ mb: 3 }}>
        <Tab label="Dashboard" />
        <Tab label="Payroll History" />
      </Tabs>

      {/* Dashboard Tab */}
      <TabPanel value={tab} index={0}>
        {statsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {/* Summary Cards with MoM Comparison */}
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: 2, boxShadow: 2, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <People sx={{ fontSize: 32, opacity: 0.9 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {stats.monthOverMonth.staffCountChange >= 0 ? (
                        <ArrowUpward sx={{ fontSize: 16 }} />
                      ) : (
                        <ArrowDownward sx={{ fontSize: 16 }} />
                      )}
                      <Typography variant="caption" sx={{ opacity: 0.9 }}>
                        {Math.abs(stats.monthOverMonth.staffCountChangePercent).toFixed(1)}%
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>Total Payrolls</Typography>
                  <Typography variant="h3" fontWeight={700}>{stats.totalPayrolls}</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8, mt: 1 }}>Year-to-Date: {stats.yearToDate.totalPayrolls}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: 2, boxShadow: 2, background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <People sx={{ fontSize: 32, opacity: 0.9 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {stats.monthOverMonth.staffCountChange >= 0 ? (
                        <ArrowUpward sx={{ fontSize: 16 }} />
                      ) : (
                        <ArrowDownward sx={{ fontSize: 16 }} />
                      )}
                      <Typography variant="caption" sx={{ opacity: 0.9 }}>
                        {Math.abs(stats.monthOverMonth.staffCountChangePercent).toFixed(1)}%
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>Total Staff</Typography>
                  <Typography variant="h3" fontWeight={700}>{stats.totalStaff}</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8, mt: 1 }}>
                    Change: {stats.monthOverMonth.staffCountChange >= 0 ? '+' : ''}{stats.monthOverMonth.staffCountChange}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: 2, boxShadow: 2, background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <AttachMoney sx={{ fontSize: 32, opacity: 0.9 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {stats.monthOverMonth.netSalaryChange >= 0 ? (
                        <ArrowUpward sx={{ fontSize: 16 }} />
                      ) : (
                        <ArrowDownward sx={{ fontSize: 16 }} />
                      )}
                      <Typography variant="caption" sx={{ opacity: 0.9 }}>
                        {Math.abs(stats.monthOverMonth.netSalaryChangePercent).toFixed(1)}%
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>Total Net Salary</Typography>
                  <Typography variant="h3" fontWeight={700}>${stats.totalNetSalary.toFixed(2)}</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8, mt: 1 }}>YTD: ${stats.yearToDate.totalNetSalary.toFixed(2)}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: 2, boxShadow: 2, background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <TrendingUp sx={{ fontSize: 32, opacity: 0.9 }} />
                    <Assessment sx={{ fontSize: 32, opacity: 0.9 }} />
                  </Box>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>Average Salary</Typography>
                  <Typography variant="h3" fontWeight={700}>${stats.averageSalary.toFixed(2)}</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8, mt: 1 }}>Per staff member</Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Monthly Trends Chart */}
            <Grid item xs={12} md={8}>
              <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ShowChart /> Monthly Salary Trends
                  </Typography>
                  {stats.monthlyTrends.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={stats.monthlyTrends}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="netSalary" stroke="#8884d8" name="Net Salary" strokeWidth={2} />
                        <Line type="monotone" dataKey="baseSalary" stroke="#82ca9d" name="Base Salary" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>No trend data available</Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Department Distribution Pie Chart */}
            <Grid item xs={12} md={4}>
              <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccountBalance /> Salary by Department
                  </Typography>
                  {stats.departmentBreakdown.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={stats.departmentBreakdown}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="totalSalary"
                        >
                          {stats.departmentBreakdown.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>No department data available</Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Department Breakdown with Progress Bars */}
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Assessment /> Department Breakdown
                  </Typography>
                  {stats.departmentBreakdown.length === 0 ? (
                    <Typography color="text.secondary">No department data available</Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {stats.departmentBreakdown.map((dept, index) => {
                        const maxSalary = stats.departmentBreakdown[0]?.totalSalary || 1;
                        const percentage = (dept.totalSalary / maxSalary) * 100;
                        return (
                          <Box key={dept.name}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="body2" fontWeight={600}>{dept.name}</Typography>
                              <Typography variant="body2" color="text.secondary">
                                ${dept.totalSalary.toFixed(2)} ({dept.count} staff)
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={percentage}
                              sx={{
                                height: 8,
                                borderRadius: 4,
                                bgcolor: 'grey.200',
                                '& .MuiLinearProgress-bar': {
                                  bgcolor: COLORS[index % COLORS.length],
                                },
                              }}
                            />
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Top Earners */}
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmojiEvents /> Top Earners
                  </Typography>
                  {stats.topEarners.length === 0 ? (
                    <Typography color="text.secondary">No earnings data available</Typography>
                  ) : (
                    <List sx={{ py: 0 }}>
                      {stats.topEarners.map((earner, index) => (
                        <React.Fragment key={index}>
                          <ListItem>
                            <ListItemAvatar>
                              <Avatar sx={{ bgcolor: COLORS[index % COLORS.length] }}>
                                {earner.name.charAt(0)}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={earner.name}
                              secondary={`${earner.department} • ${earner.month}`}
                            />
                            <Typography variant="h6" fontWeight={700} color="success.main">
                              ${earner.salary.toFixed(2)}
                            </Typography>
                          </ListItem>
                          {index < stats.topEarners.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Financial Overview */}
            <Grid item xs={12} md={4}>
              <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AttachMoney /> Financial Overview
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">Base Salary</Typography>
                      <Typography variant="h6" fontWeight={700}>${stats.totalBaseSalary.toFixed(2)}</Typography>
                    </Paper>
                    <Paper sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">Tax</Typography>
                      <Typography variant="h6" fontWeight={700} color="error.main">${stats.totalTax.toFixed(2)}</Typography>
                    </Paper>
                    <Paper sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">Allowances</Typography>
                      <Typography variant="h6" fontWeight={700} color="success.main">${stats.totalAllowances.toFixed(2)}</Typography>
                    </Paper>
                    <Paper sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">Deductions</Typography>
                      <Typography variant="h6" fontWeight={700} color="warning.main">${stats.totalDeductions.toFixed(2)}</Typography>
                    </Paper>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Recent Payrolls */}
            <Grid item xs={12} md={8}>
              <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarToday /> Recent Payrolls
                  </Typography>
                  {stats.recentPayrolls.length === 0 ? (
                    <Typography color="text.secondary">No recent payrolls</Typography>
                  ) : (
                    <Grid container spacing={2}>
                      {stats.recentPayrolls.map((payroll) => (
                        <Grid item xs={12} sm={6} md={4} key={payroll.id}>
                          <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', '&:hover': { boxShadow: 3 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                              <Typography variant="body2" fontWeight={700}>{payroll.payroll_month}</Typography>
                              <Chip size="small" label={`${payroll.staff_count} staff`} color="primary" />
                            </Box>
                            <Typography variant="h6" fontWeight={700} color="success.main">
                              ${parseFloat(payroll.total_net_salary || 0).toFixed(2)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Base: ${parseFloat(payroll.total_base_salary || 0).toFixed(2)}
                            </Typography>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </TabPanel>

      {/* Payroll History Tab */}
      <TabPanel value={tab} index={1}>

      {/* Payroll Table */}
      <Paper sx={{ borderRadius: 2, boxShadow: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Payroll Month</TableCell>
              <TableCell align="right">Staff Count</TableCell>
              <TableCell align="right">Total Base Salary</TableCell>
              <TableCell align="right">Total Tax</TableCell>
              <TableCell align="right">Total Allowances</TableCell>
              <TableCell align="right">Total Deductions</TableCell>
              <TableCell align="right">Total Net Salary</TableCell>
              <TableCell align="right">Created At</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} sx={{ textAlign: 'center', py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : payrolls.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} sx={{ textAlign: 'center', py: 4 }}>
                  <Alert severity="info">No payroll records found</Alert>
                </TableCell>
              </TableRow>
            ) : (
              payrolls.map((payroll) => (
                <TableRow key={payroll.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {payroll.payroll_month}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{payroll.staff_count}</TableCell>
                  <TableCell align="right">${parseFloat(payroll.total_base_salary || 0).toFixed(2)}</TableCell>
                  <TableCell align="right">${parseFloat(payroll.total_tax || 0).toFixed(2)}</TableCell>
                  <TableCell align="right">${parseFloat(payroll.total_allowances || 0).toFixed(2)}</TableCell>
                  <TableCell align="right">${parseFloat(payroll.total_deductions || 0).toFixed(2)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>${parseFloat(payroll.total_net_salary || 0).toFixed(2)}</TableCell>
                  <TableCell align="right">
                    {new Date(payroll.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      <IconButton
                        size="small"
                        onClick={() => handleViewDetails(payroll.id)}
                        title="View Details"
                      >
                        <Visibility />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleExportPayroll(payroll)}
                        title="Export"
                      >
                        <Download />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeletePayroll(payroll.id)}
                        title="Delete"
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>
      </TabPanel>

      {/* Payroll Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => {
          setDetailDialogOpen(false);
          setSelectedPayroll(null);
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Payroll Details - {selectedPayroll?.payroll_month}
        </DialogTitle>
        <DialogContent>
          {selectedPayroll && (
            <Box>
              {/* Summary */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Summary
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="caption" color="text.secondary">Total Staff</Typography>
                    <Typography variant="h5" fontWeight={700}>{selectedPayroll.staff_count}</Typography>
                  </Paper>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="caption" color="text.secondary">Total Net Salary</Typography>
                    <Typography variant="h5" fontWeight={700} color="success.main">
                      ${parseFloat(selectedPayroll.total_net_salary || 0).toFixed(2)}
                    </Typography>
                  </Paper>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="caption" color="text.secondary">Tax Rate</Typography>
                    <Typography variant="h5" fontWeight={700}>{selectedPayroll.tax_rate}%</Typography>
                  </Paper>
                </Box>
              </Box>

              {/* Items Table */}
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Payroll Items
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Staff Name</TableCell>
                    <TableCell>Account Number</TableCell>
                    <TableCell>Department</TableCell>
                    <TableCell align="right">Base Salary</TableCell>
                    <TableCell align="right">Tax</TableCell>
                    <TableCell align="right">Allowances</TableCell>
                    <TableCell align="right">Deductions</TableCell>
                    <TableCell align="right">Net Salary</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedPayroll.items?.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.staff_name}</TableCell>
                      <TableCell>{item.account_number || '-'}</TableCell>
                      <TableCell>{item.department}</TableCell>
                      <TableCell align="right">${parseFloat(item.base_salary || 0).toFixed(2)}</TableCell>
                      <TableCell align="right">${parseFloat(item.tax || 0).toFixed(2)}</TableCell>
                      <TableCell align="right">${parseFloat(item.total_allowances || 0).toFixed(2)}</TableCell>
                      <TableCell align="right">${parseFloat(item.total_deductions || 0).toFixed(2)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        ${parseFloat(item.net_salary || 0).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDetailDialogOpen(false);
            setSelectedPayroll(null);
          }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PayrollPage;
